import React from 'react';

import { Box, Typography, Stack, useMediaQuery, useTheme } from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';
import SupportLink from '@ivy/components/atoms/SupportLink';
import NavTemplate from '@ivy/components/templates/NavTemplate';
import { IVY_LOGO_TOP } from '@ivy/constants/brand';

const ContactUs = ({ sx }) => {
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('sm'));
	return (
		<Stack
			component='section'
			justifyContent='center'
			alignItems='center'
			sx={[
				{
					position: 'relative',
					py: {
						xs: 3,
						md: 5,
					},
				},
				sx,
			]}
		>
			<Box
				sx={{
					bgcolor: 'primary.verysubtle',
					// Full bleed
					width: '100vw',
					position: 'absolute',
					left: '50%',
					right: '50%',
					ml: '-50vw',
					mr: '-50vw',
					height: '100%',
					zIndex: -1,
				}}
			/>
			<Typography
				variant={isSm ? 'h5' : 'h4'}
				component='p'
				color='primary.main'
				textAlign='center'
				gutterBottom
			>
				Have a question or feedback?
			</Typography>
			<Typography variant='body1' textAlign='center' gutterBottom>
				You can{' '}
				<RouteLink to={RouteLink.routes.FAQ_LIST}>
					check out our FAQs,
				</RouteLink>{' '}
				or <SupportLink>send us a message</SupportLink>.
			</Typography>
		</Stack>
	);
};

const Intro = ({ sx }) => {
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('sm'));

	return (
		<Box
			component='section'
			sx={[
				{
					position: 'relative',
				},
				sx,
			]}
		>
			<Box
				sx={{
					bgcolor: 'primary.verysubtle',
					// Full bleed
					width: '100vw',
					position: 'absolute',
					left: '50%',
					right: '50%',
					ml: '-50vw',
					mr: '-50vw',
					height: '100%',
					zIndex: -1,
				}}
			/>
			<Box
				mx='auto'
				sx={{
					py: 5,
				}}
			>
				<Typography
					component='h1'
					variant={isSm ? 'h4' : 'h3'}
					color='primary.main'
					gutterBottom
				>
					What is Ivy Clinicians?
				</Typography>
				<Typography variant='body1' mb={1}>
					This invaluable resource originated with{' '}
					<RouteLink to='https://www.linkedin.com/in/leoncadelmanmd'>
						Leon Adelman, MD, MBA, FACEP, FAAEM
					</RouteLink>
					, and{' '}
					<RouteLink to='https://www.linkedin.com/in/matthew-simpson-937110a3'>
						Matt Simpson
					</RouteLink>
					, who dreamt of a clear, simple way for clinicians and recruiters
					alike to find the right fit in the job market, aligned with goals,
					working conditions, and philosophies.
				</Typography>
				<Typography variant='body1'>
					Now run by EMRA, the platform remains true to those standards for the
					health care employment sector – and expands on it through{' '}
					<RouteLink
						to={{
							pathname: RouteLink.routes.ROOT,
							whitelabel: 'emra',
						}}
						openInNewTab
					>
						EMRA Match
					</RouteLink>
					, the definitive search tool for EM-bound candidates to find their
					dream residency programs, fellowship opportunities, and clerkship
					openings.
				</Typography>
			</Box>
		</Box>
	);
};

const AboutCompany = ({ sx }) => {
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('sm'));

	return (
		<Box
			component='section'
			sx={[
				{
					position: 'relative',
				},
				sx,
			]}
		>
			<Box
				mx='auto'
				sx={{
					py: 5,
				}}
			>
				<Typography
					component='h2'
					variant={isSm ? 'h5' : 'h4'}
					color='primary.main'
					gutterBottom
				>
					Ivy Clinicians is led by{' '}
					<Typography
						variant={isSm ? 'h5' : 'h4'}
						color='secondary.main'
						component='span'
					>
						physicians, PAs, and nurse practitioners
					</Typography>{' '}
					dedicated to improving the healthcare job market.
				</Typography>
				<Typography variant='body1'>
					The clinician job market is too hard to navigate. For providers, it’s
					difficult to find out which group staffs an ER, who the right contact
					person is, and what the working conditions are at the site. For
					recruiters, finding clinician matches is difficult because providers
					don’t have a simple, safe way to indicate interest in particular jobs
					and to communicate their priorities. Ivy is designed to connect
					clinicians with in-house recruiters so both find what they’re looking
					for.
				</Typography>
			</Box>
		</Box>
	);
};

const WhyIvy = ({ sx }) => {
	return (
		<Box
			component='section'
			sx={[
				{
					pt: 5,
					pb: 10,
				},
				sx,
			]}
		>
			<Box
				sx={{
					mx: 'auto',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						flexDirection: {
							xs: 'column',
							md: 'row',
						},
						mx: 'auto',
						alignItems: 'center',
					}}
				>
					<Box
						flexBasis='calc(50% - 32px)'
						mt={{
							xs: 5,
							md: 0,
						}}
					>
						<Box
							component='img'
							src='https://assets.ivyclinicians.io/images/jtodd.jpeg'
							sx={{
								objectFit: {
									xs: 'contain',
									md: 'cover',
								},
								borderRadius: (theme) => `${theme.shape.borderRadius}px`,
								width: '100%',
								minHeight: {
									md: '500px',
								},
							}}
						/>
					</Box>
					<Box
						flexBasis='calc(50% - 32px)'
						display='flex'
						flexDirection='column'
						justifyContent='center'
						height='100%'
						mt={{
							xs: 5,
							md: 0,
						}}
					>
						<Typography
							variant='h4'
							component='h2'
							color='primary.main'
							gutterBottom
							textAlign={{
								xs: 'center',
								md: 'left',
							}}
						>
							Why Ivy?
						</Typography>
						<Typography variant='body1'>
							Ivy’s marketplace technology matches the right clinician with the
							right employer. Clinicians, employers, and patients win when
							physicians, PAs, and nurse practitioners find jobs they love.
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

const Difference = ({ sx }) => {
	return (
		<Box
			component='section'
			sx={[
				{
					position: 'relative',
					pb: 5,
				},
				sx,
			]}
		>
			<Box
				sx={{
					mx: 'auto',
					display: 'flex',
					flexDirection: {
						xs: 'column-reverse',
						md: 'row',
					},
					alignItems: {
						xs: 'initial',
						md: 'center',
					},
					py: 5,
				}}
			>
				<Box
					flexBasis='50%'
					mt={{
						xs: 5,
						md: 0,
					}}
				>
					<Box
						maxWidth={{
							md: '440px',
						}}
					>
						<Typography
							variant='h4'
							component='h2'
							color='primary.main'
							gutterBottom
							textAlign={{
								xs: 'center',
								md: 'left',
							}}
						>
							What makes Ivy different?
						</Typography>
						<Typography variant='body1' color='primary.main'>
							Clinicians go first on Ivy. Clinicians can search and connect with
							any employer in their specialty so they can take great care of
							patients.
						</Typography>
					</Box>
				</Box>
				<Box flexBasis='50%' display='flex' justifyContent='center'>
					<Box
						component='img'
						src={IVY_LOGO_TOP}
						width='300px'
						maxWidth='100%'
					/>
				</Box>
			</Box>
		</Box>
	);
};

const AboutUs = () => {
	return (
		<NavTemplate
			maxWidth='lg'
			showFooter
			pageTitle='About Us'
			pageDescription='Learn more about the team and vision behind Ivy Clinicians.'
			showSupportLauncher
			pathIsCanonical
			hideBackNav
		>
			<Box sx={{ mb: 5 }}>
				<Intro />
				<AboutCompany />
				<WhyIvy />
				<Difference />
				<ContactUs />
			</Box>
		</NavTemplate>
	);
};

export default AboutUs;
